/**
 * SingleOptionFilter - Select one option from many
 *
 * When you select an option, it deselects any previous selection.
 * Used for: Activity period, Admin selection, etc.
 */

import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BottomSheet } from '../../core/BottomSheet';
import { useState } from 'react';

export interface SingleOption {
  value: string;
  label: string;
}

export interface SingleOptionFilterProps {
  /** Available options */
  options: SingleOption[];
  /** Currently selected value ('all' for none) */
  value: string;
  /** Callback when selection changes */
  onChange: (value: string) => void;
  /** Filter label */
  label?: string;
  /** Bottom sheet title */
  sheetTitle?: string;
  /** Show "All" option */
  showAllOption?: boolean;
  /** "All" option label */
  allLabel?: string;
  /** Disabled state */
  disabled?: boolean;
}

export function SingleOptionFilter({
  options,
  value,
  onChange,
  label,
  sheetTitle,
  showAllOption = true,
  allLabel = 'All',
  disabled = false,
}: SingleOptionFilterProps) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = options.find((o) => o.value === value);
  const displayText = value === 'all' ? allLabel : selectedOption?.label || allLabel;

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className="space-y-2">
      {label && (
        <span className="text-sm font-medium text-slate-600">{label}</span>
      )}

      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(true)}
        disabled={disabled}
        className={cn(
          'px-4 py-2.5 rounded-full',
          'bg-white border border-slate-200',
          'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
          'text-sm font-medium text-slate-700',
          'hover:border-slate-300 active:bg-slate-50 transition-all',
          'focus:outline-none focus:ring-2 focus:ring-sky-500',
          value !== 'all' && 'border-sky-300 text-sky-600',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        {displayText}
      </button>

      {/* Bottom sheet */}
      <BottomSheet
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={sheetTitle || label || 'Select'}
      >
        <div className="space-y-1">
          {/* All option */}
          {showAllOption && (
            <button
              type="button"
              onClick={() => handleSelect('all')}
              className={cn(
                'w-full flex items-center justify-between px-4 py-4 rounded-xl',
                'text-left text-base transition-colors',
                'active:bg-slate-100',
                value === 'all'
                  ? 'bg-sky-50 text-sky-600'
                  : 'text-slate-800 hover:bg-slate-50'
              )}
            >
              <span>{allLabel}</span>
              {value === 'all' && <Check className="h-5 w-5" />}
            </button>
          )}

          {/* Options */}
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={cn(
                  'w-full flex items-center justify-between px-4 py-4 rounded-xl',
                  'text-left text-base transition-colors',
                  'active:bg-slate-100',
                  isSelected
                    ? 'bg-sky-50 text-sky-600'
                    : 'text-slate-800 hover:bg-slate-50'
                )}
              >
                <span>{option.label}</span>
                {isSelected && <Check className="h-5 w-5" />}
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </div>
  );
}
